import { useState, useEffect } from 'react'
import type { PipelineCard } from '@1130-studio/shared'
import { Button } from '../ui/Button'
import { ConfirmModal } from '../ui/ConfirmModal'
import { cn, formatDate, formatRelativeTime } from '../../lib/utils'

interface CardDetailModalProps {
  card: PipelineCard | null
  isOpen: boolean
  onClose: () => void
  onSave?: (id: string, data: { specification?: string; notes?: string }) => void
  onDelete?: (id: string, type: 'lead' | 'project') => void
  activities?: Array<{
    id: string
    type: string
    content: string
    createdAt: Date | string
  }>
}

const sourceLabels: Record<string, string> = {
  website: 'Сайт',
  telegram: 'Telegram',
  manual: 'Вручную',
  referral: 'Реферал',
}

const priorityLabels: Record<string, string> = {
  low: 'Низкий',
  medium: 'Средний',
  high: 'Высокий',
  urgent: 'Срочный',
}

const priorityColors: Record<string, string> = {
  low: 'bg-text-tertiary',
  medium: 'bg-accent-blue',
  high: 'bg-accent-orange',
  urgent: 'bg-red-500',
}

export function CardDetailModal({
  card,
  isOpen,
  onClose,
  onSave,
  onDelete,
  activities = [],
}: CardDetailModalProps) {
const [notes, setNotes] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (card) {
      setNotes(card.description || '')
    }
  }, [card])

  if (!isOpen || !card) return null

  const isLead = card.type === 'lead'
  const isProject = card.type === 'project'

  const handleSave = async () => {
    if (!onSave) return
    setIsSaving(true)
    try {
      await onSave(card.id, { specification: notes, notes })
      onClose()
    } finally {
      setIsSaving(false)
    }
  }

const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    if (!onDelete) return
    setIsDeleting(true)
    try {
      await onDelete(card.id, card.type)
      setShowDeleteConfirm(false)
      onClose()
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
      <div className="glass-card w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'px-2.5 py-1 rounded text-xs font-semibold uppercase tracking-wider',
                isLead && 'bg-accent-orange/20 text-accent-orange',
                isProject && 'bg-accent-blue/20 text-accent-blue'
              )}
            >
              {isLead ? 'Заявка' : 'Проект'}
            </div>
            <span className="text-text-tertiary text-sm">
              {formatRelativeTime(card.createdAt)}
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-text-secondary hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Title */}
          <div>
            <h2 className="text-xl font-bold text-white mb-1">{card.name}</h2>
            <p className="text-sm text-text-secondary">
              Создано {formatDate(card.createdAt)}
            </p>
          </div>

          <div className="h-px bg-white/10" />

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-6">
            {/* Lead-specific info */}
            {isLead && (
              <>
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                    Контакт
                  </label>
                  <div className="space-y-1.5">
                    {card.email && (
                      <div className="flex items-center gap-2 text-sm text-text-primary">
                        <svg className="w-4 h-4 text-text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                          <polyline points="22,6 12,13 2,6"/>
                        </svg>
                        <a href={`mailto:${card.email}`} className="hover:text-accent-lime transition-colors">
                          {card.email}
                        </a>
                      </div>
                    )}
                    {card.phone && (
                      <div className="flex items-center gap-2 text-sm text-text-primary">
                        <svg className="w-4 h-4 text-text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                        </svg>
                        <a href={`tel:${card.phone}`} className="hover:text-accent-lime transition-colors">
                          {card.phone}
                        </a>
                      </div>
                    )}
                    {card.telegram && (
                      <div className="flex items-center gap-2 text-sm text-text-primary">
                        <svg className="w-4 h-4 text-text-tertiary" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.751-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.015 3.333-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.141.121.099.155.232.17.324.015.092.034.303.019.467z"/>
                        </svg>
                        <a href={`https://t.me/${card.telegram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="hover:text-accent-lime transition-colors">
                          {card.telegram}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                    Источник
                  </label>
                  <div className="text-sm text-text-primary">
                    {sourceLabels[card.source || ''] || card.source || 'Не указан'}
                  </div>
                </div>

                {card.message && (
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                      Сообщение
                    </label>
                    <div className="text-sm text-text-primary bg-white/5 rounded-lg p-4 italic">
                      "{card.message}"
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Project-specific info */}
            {isProject && (
              <>
                {card.client && (
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                      Клиент
                    </label>
                    <div className="space-y-1">
                      <div className="text-sm font-medium text-text-primary">
                        {card.client.companyName || card.client.contactName}
                      </div>
                      {card.client.email && (
                        <div className="text-xs text-text-secondary">{card.client.email}</div>
                      )}
                      {card.client.phone && (
                        <div className="text-xs text-text-secondary">{card.client.phone}</div>
                      )}
                    </div>
                  </div>
                )}

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                    Бюджет
                  </label>
                  <div className="text-sm text-text-primary font-mono">
                    {card.budget ? `${parseFloat(card.budget).toLocaleString('ru-RU')} ₽` : 'Не указан'}
                  </div>
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                    Дедлайн
                  </label>
                  <div className="text-sm text-text-primary">
                    {card.deadline ? formatDate(card.deadline) : 'Не указан'}
                  </div>
                </div>

                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
                    Приоритет
                  </label>
                  <div className="flex items-center gap-2">
                    <div className={cn('w-2 h-2 rounded-full', priorityColors[card.priority || 'medium'])} />
                    <span className="text-sm text-text-primary">
                      {priorityLabels[card.priority || 'medium']}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="h-px bg-white/10" />

          {/* Notes / Specification */}
          <div>
            <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
              {isLead ? 'Спецификация' : 'Заметки'}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full h-32 px-4 py-3 bg-bg-tertiary border border-border-medium rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-accent-lime/50 focus:ring-1 focus:ring-accent-lime/20 resize-none"
              placeholder={isLead ? 'Добавьте спецификацию или заметки...' : 'Добавьте заметки о проекте...'}
            />
          </div>

          {/* Activity History */}
          {activities.length > 0 && (
            <>
              <div className="h-px bg-white/10" />
              <div>
                <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-3">
                  История активности
                </label>
                <div className="space-y-3">
                  {activities.map((activity) => (
                    <div key={activity.id} className="flex items-start gap-3 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full bg-text-tertiary mt-2 flex-shrink-0" />
                      <div className="flex-1">
                        <span className="text-text-secondary">{activity.content}</span>
                        <span className="text-text-tertiary ml-2">
                          {formatRelativeTime(activity.createdAt)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-white/10">
<Button
            variant="danger"
            size="sm"
            onClick={handleDeleteClick}
          >
            Удалить
          </Button>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              onClick={onClose}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              loading={isSaving}
            >
              Сохранить
            </Button>
</div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Удалить карточку?"
        message={`Вы уверены, что хотите удалить "${card.name}"? Это действие нельзя отменить.`}
        confirmText="Удалить"
        cancelText="Отмена"
        variant="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        loading={isDeleting}
      />
    </div>
  )
}
